<template>
  <div ref="recaptchaContainer" class="recaptcha-container"></div>
</template>

<script>
const RECAPTCHA_SCRIPT_URL = "https://www.google.com/recaptcha/api.js";

export default {
  name: "BaseReCaptcha",
  props: {
    siteKey: {
      type: String,
      required: true,
    },
    theme: {
      type: String,
      default: "light",
      validator: (value) => ["light", "dark"].includes(value),
    },
    size: {
      type: String,
      default: "normal",
      validator: (value) => ["normal", "compact"].includes(value),
    },
    tabindex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      widgetId: null,
      isScriptLoaded: false,
    };
  },
  mounted() {
    this.loadRecaptchaScript().then(() => {
      this.renderRecaptcha();
    });
  },
  beforeDestroy() {
    if (this.widgetId !== null && window.grecaptcha) {
      window.grecaptcha.reset(this.widgetId);
    }
  },
  methods: {
    loadRecaptchaScript() {
      return new Promise((resolve) => {
        if (window.grecaptcha && window.grecaptcha.render) {
          this.isScriptLoaded = true;
          resolve();
          return;
        }

        if (document.querySelector(`script[src*="recaptcha"]`)) {
          this.waitForGrecaptcha(resolve);
          return;
        }

        const script = document.createElement("script");
        script.src = `${RECAPTCHA_SCRIPT_URL}?render=explicit`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          this.waitForGrecaptcha(resolve);
        };
        document.head.appendChild(script);
      });
    },
    waitForGrecaptcha(resolve) {
      const checkInterval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(checkInterval);
          this.isScriptLoaded = true;
          resolve();
        }
      }, 100);
    },
    renderRecaptcha() {
      if (!this.isScriptLoaded || !this.$refs.recaptchaContainer) {
        return;
      }

      this.widgetId = window.grecaptcha.render(this.$refs.recaptchaContainer, {
        sitekey: this.siteKey,
        theme: this.theme,
        size: this.size,
        tabindex: this.tabindex,
        callback: this.onVerify,
        "expired-callback": this.onExpired,
        "error-callback": this.onError,
      });
    },
    onVerify(token) {
      this.$emit("verify", token);
    },
    onExpired() {
      this.$emit("expired");
      this.$emit("verify", null);
    },
    onError() {
      this.$emit("error");
      this.$emit("verify", null);
    },
    reset() {
      if (this.widgetId !== null && window.grecaptcha) {
        window.grecaptcha.reset(this.widgetId);
        this.$emit("verify", null);
      }
    },
    getResponse() {
      if (this.widgetId !== null && window.grecaptcha) {
        return window.grecaptcha.getResponse(this.widgetId);
      }
      return null;
    },
  },
};
</script>

<style scoped>
.recaptcha-container {
  display: flex;
  justify-content: center;
  margin: 1rem 0;
}
</style>
